import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useCallback, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { getActivationOrigin } from '@/shared/lib/activationOrigin';
import { cx } from '@/shared/lib/cx';

import styles from './Dialog.module.scss';

type RadixContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content>;

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<DialogSize, string> = {
  sm: styles['dialog--sm'] ?? '',
  md: styles['dialog--md'] ?? '',
  lg: styles['dialog--lg'] ?? '',
  xl: styles['dialog--xl'] ?? '',
};

/** Насколько окно подтягивается к кнопке в начале движения. */
const TRAVEL_RATIO = 0.4;

/**
 * Корень диалога. Управляет открытием — и сам, и снаружи через `open`.
 *
 * Поверх примитива Radix, а не своими руками, потому что правильный диалог
 * состоит не из затемнения и рамки, а из ловушки фокуса, возврата фокуса на
 * вызвавшую кнопку, блокировки прокрутки под собой, `aria-modal`, закрытия
 * по Escape и скрытия остального содержимого от скринридера. Каждый из этих
 * пунктов легко забыть и невозможно заметить глазами.
 */
export const Dialog = RadixDialog.Root;

export const DialogTrigger = RadixDialog.Trigger;

export const DialogClose = RadixDialog.Close;

// Наследуем свойства примитива Radix, а не обычного div: у него есть
// обработчики закрытия и наведения фокуса, которые нужны наружу, а `title`
// у div означает совсем другое — всплывающую подсказку.
export interface DialogContentProps extends Omit<RadixContentProps, 'title'> {
  /**
   * Заголовок диалога. Обязателен: без него скринридер объявляет окно
   * безымянным, и пользователь не понимает, куда его перенесло.
   */
  title: ReactNode;
  /** Пояснение под заголовком. Тоже зачитывается при открытии. */
  description?: ReactNode;
  /** Спрятать заголовок визуально, оставив его скринридеру. */
  titleHidden?: boolean;
  size?: DialogSize;
  /**
   * Разрешено ли закрывать окно по Escape и клику мимо.
   *
   * Для форм с набранными данными нужно `false`: промах мышью не должен
   * стирать работу. Крестик и кнопка «Отмена» при этом остаются — путь
   * наружу обязан существовать всегда.
   */
  dismissible?: boolean;
  /** Убрать крестик — только если закрытие доступно иначе. */
  hideCloseButton?: boolean;
  /**
   * Без шапки, тела и подвала: окно даёт только затемнение, ловушку
   * фокуса и движение из точки клика, а всё внутри рисует вызывающий —
   * например, окно задачи со своей шапкой. Заголовок при этом всё равно
   * обязателен и уходит скринридеру.
   */
  bare?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Готовит окно к раскрытию из точки, где было действие.
 *
 * Окно вырастает из кнопки, но встаёт по центру: связь с нажатием видна, а
 * композиция остаётся привычной — люди приучены, что важное появляется по
 * центру, и прыгающее по экрану окно каждый раз сбивает.
 *
 * Когда точки отсчёта нет — открыли с клавиатуры, программно, по событию с
 * сервера — переменные остаются значениями по умолчанию, и окно раскрывается
 * из собственного центра. Это не отдельный режим, а запасной путь.
 *
 * Возвращает ref-функцию, а не обычную ссылку с эффектом. Разница
 * принципиальная: этот компонент монтируется вместе с `<Dialog>`, а само
 * окно Radix создаёт только при открытии. Эффект отработал бы в момент,
 * когда узла ещё нет, и больше бы не повторился — окно осталось бы вовсе
 * без движения. Ref-функция же вызывается ровно тогда, когда узел появился,
 * и по-прежнему до отрисовки.
 */
function useGrowFromOrigin() {
  return useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return;
    }

    const origin = getActivationOrigin();

    if (origin) {
      const box = node.getBoundingClientRect();

      // Точка отсчёта — место кнопки относительно самого окна. Масштаб
      // растёт именно оттуда, поэтому движение читается как продолжение
      // нажатия, а не как отдельное появление.
      node.style.setProperty('--dialog-origin-x', `${String(Math.round(origin.x - box.left))}px`);
      node.style.setProperty('--dialog-origin-y', `${String(Math.round(origin.y - box.top))}px`);

      // Плюс окно начинает движение подтянутым в сторону кнопки — но лишь
      // частично: полный перелёт через весь экран выглядит суетой.
      const shiftX = (origin.x - (box.left + box.width / 2)) * TRAVEL_RATIO;
      const shiftY = (origin.y - (box.top + box.height / 2)) * TRAVEL_RATIO;

      node.style.setProperty('--dialog-shift-x', `${String(Math.round(shiftX))}px`);
      node.style.setProperty('--dialog-shift-y', `${String(Math.round(shiftY))}px`);
    }

    // Движение включается только после того, как значения проставлены:
    // иначе первый кадр успел бы пройти с неверной точкой отсчёта.
    node.dataset['ready'] = '';
  }, []);
}

export function DialogContent({
  title,
  description,
  titleHidden = false,
  size = 'md',
  dismissible = true,
  hideCloseButton = false,
  bare = false,
  footer,
  className,
  children,
  ...rest
}: DialogContentProps) {
  const contentRef = useGrowFromOrigin();

  // Запрет закрытия навешивается после остальных свойств: он не настройка
  // по умолчанию, а требование вызывающего кода, и перебить его случайным
  // обработчиком снаружи нельзя.
  const guard: Partial<RadixContentProps> = dismissible
    ? {}
    : {
        onEscapeKeyDown: (event) => {
          event.preventDefault();
        },
        onPointerDownOutside: (event) => {
          event.preventDefault();
        },
        onInteractOutside: (event) => {
          event.preventDefault();
        },
      };

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={styles['dialog__overlay']} />
      <RadixDialog.Content
        ref={contentRef}
        className={cx(styles['dialog'], SIZE[size], bare && styles['dialog--bare'], className)}
        {...rest}
        {...guard}
      >
        {bare ? (
          <>
            <RadixDialog.Title className={styles['dialog__title--hidden']}>
              {title}
            </RadixDialog.Title>
            {children}
          </>
        ) : (
          <>
            <div className={styles['dialog__head']}>
              <div className={styles['dialog__heading']}>
                <RadixDialog.Title
                  className={cx(
                    styles['dialog__title'],
                    titleHidden && styles['dialog__title--hidden'],
                  )}
                >
                  {title}
                </RadixDialog.Title>

                {description !== undefined && (
                  <RadixDialog.Description className={styles['dialog__description']}>
                    {description}
                  </RadixDialog.Description>
                )}
              </div>

              {!hideCloseButton && (
                <RadixDialog.Close className={styles['dialog__close']} aria-label="Закрыть">
                  <X size={17} />
                </RadixDialog.Close>
              )}
            </div>

            {/* Прокручивается только тело: шапка с заголовком и подвал с
            действиями должны оставаться на виду в длинных формах. */}
            <div className={styles['dialog__body']}>{children}</div>

            {footer !== undefined && <div className={styles['dialog__footer']}>{footer}</div>}
          </>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
